import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'

export interface CaptureCardButton {
  text: string
  type: 'url' | 'postback'
  value: string
}

export interface CaptureCard {
  id: string
  name: string
  title: string
  subtitle: string | null
  imageUrl: string
  imageHasFilter: boolean
  buttonText: string
  buttonUrl: string | null
  buttonType: 'url' | 'postback'
  buttonPayload: string | null
  secondaryButtonText: string | null
  secondaryButtonUrl: string | null
  secondaryButtonType: 'url' | 'postback' | null
  secondaryButtonPayload: string | null
  buttons: CaptureCardButton[]
  publicReplyText: string | null
  isActive: boolean
  sentCount: number
  clickCount: number
  todayClicks: number
  errorCount: number
  lastSentAt: string | null
  lastClickAt: string | null
  accountIds: string[]
  createdAt: string | null
  updatedAt: string | null
}

export interface CaptureCardStatsPoint {
  date: string
  label: string
  clicks: number
}

export interface CaptureCardStats {
  todayClicks: number
  totalClicks: number
  cardsWithClicksToday: number
  chart: CaptureCardStatsPoint[]
  topCardsToday: Array<{
    id: string
    name: string
    todayClicks: number
    totalClicks: number
  }>
}

export interface CaptureCardAccount {
  id: string
  name: string
  pageId: string
  igUserId: string
  isActive: boolean
  fbActive: boolean
}

export interface CaptureCardPayload {
  name: string
  title: string
  subtitle?: string | null
  imageUrl: string
  imageHasFilter?: boolean
  buttonText?: string
  buttonUrl?: string | null
  buttons: CaptureCardButton[]
  publicReplyText?: string | null
  isActive?: boolean
  accountIds?: string[]
}

export function useCaptureCards(refetchInterval: number | false = false) {
  return useQuery<CaptureCard[]>({
    queryKey: ['capture-cards'],
    queryFn: () => api.get<CaptureCard[]>('/capture-cards'),
    refetchInterval,
  })
}

export function useCaptureCardStats(refetchInterval: number | false = false) {
  return useQuery<CaptureCardStats>({
    queryKey: ['capture-cards', 'stats'],
    queryFn: () => api.get<CaptureCardStats>('/capture-cards/stats'),
    refetchInterval,
  })
}

export function useCaptureCardAccounts() {
  return useQuery<CaptureCardAccount[]>({
    queryKey: ['capture-cards', 'accounts'],
    queryFn: () => api.get<CaptureCardAccount[]>('/capture-cards/available-accounts'),
  })
}

export function useCreateCaptureCard() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: CaptureCardPayload) =>
      api.post<CaptureCard>('/capture-cards', payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['capture-cards'] })
      qc.invalidateQueries({ queryKey: ['capture-cards', 'stats'] })
    },
  })
}

export function useUpdateCaptureCard() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: CaptureCardPayload }) =>
      api.put<CaptureCard>(`/capture-cards/${id}`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['capture-cards'] })
      qc.invalidateQueries({ queryKey: ['capture-cards', 'stats'] })
    },
  })
}

export function useDeleteCaptureCard() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete<{ ok: boolean }>(`/capture-cards/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['capture-cards'] })
      qc.invalidateQueries({ queryKey: ['capture-cards', 'stats'] })
    },
  })
}

export function useToggleCaptureCard() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      api.patch<{ ok: boolean; isActive: boolean }>(`/capture-cards/${id}/toggle`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['capture-cards'] })
      qc.invalidateQueries({ queryKey: ['capture-cards', 'stats'] })
    },
  })
}

export function useBulkUpdateUrl() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { newUrl: string; buttonIndex?: number }) =>
      api.post<{ ok: boolean; updated: number }>('/capture-cards/bulk-update-url', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['capture-cards'] })
    },
  })
}

export function useImportPresets() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (presetIds: string[]) =>
      api.post<{ ok: boolean; created: number; ids: string[] }>('/capture-cards/import-presets', { presetIds }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['capture-cards'] })
    },
  })
}

export function useBulkActivate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      api.post<{ ok: boolean; activated: number }>('/capture-cards/bulk-activate', {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['capture-cards'] })
    },
  })
}

export function useBulkDeactivate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      api.post<{ ok: boolean; deactivated: number }>('/capture-cards/bulk-deactivate', {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['capture-cards'] })
    },
  })
}

export function useBulkAssignAll() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      api.post<{ ok: boolean; assigned: number; cards: number; accounts: number }>('/capture-cards/bulk-assign-all', {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['capture-cards'] })
    },
  })
}
